<template>
  <div class="monitoring-dashboard">
    <NavBar v-if="terms == 1" />
    <AuthBySessionExpired />
    <DashboardMonitoringConfig @save="save" />

    <Terms v-if="terms == 0" />

    <div v-if="terms == 1" class="monitoring-container">
      <div class="monitoring-header">
        <div class="header-content">
          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-desktop title-icon"></i>
              Dashboard de Monitoramento
            </h1>
            <p class="page-subtitle">
              Acompanhamento em tempo real dos residentes
            </p>
          </div>

          <div class="auto-update-info">
            <div class="update-indicator">
              <div class="indicator-dot" :class="{ active: !loading }"></div>
              <div class="update-text">
                <span class="update-label">Próxima atualização</span>
                <span class="update-time">{{ nextUpdateText }}</span>
              </div>
            </div>
          </div>

          <div class="actions-section">
            <v-btn
              class="btn-modern btn-modern-config"
              @click="handleModalConfig"
              small
            >
              <i class="fas fa-cogs mr-1"></i>
              Config
            </v-btn>
            <v-btn
              class="btn-modern btn-modern-refresh"
              @click="retrieve"
              :disabled="loading"
              small
            >
              <i class="fas fa-sync-alt mr-1"></i>
              {{ loading ? "Atualizando" : "Atualizar" }}
            </v-btn>
            <v-btn
              class="btn-modern btn-modern-stop"
              @click="handleStopSpeak"
              small
            >
              <i class="fas fa-volume-mute mr-1"></i>
              Silenciar
            </v-btn>
          </div>
        </div>
      </div>

      <div class="monitoring-content">
        <b-overlay
          :show="loading && residents.length === 0"
          opacity="0.99"
          class="loading-overlay"
        >
          <div class="stats-summary">
            <div class="stat-card total-residents">
              <div class="stat-content">
                <div class="stat-number">{{ totalResidents }}</div>
                <div class="stat-label">Total</div>
              </div>
              <div class="stat-icon">
                <i class="fas fa-users"></i>
              </div>
            </div>

            <div class="stat-card with-prescription">
              <div class="stat-content">
                <div class="stat-number">{{ residentsWithPrescription }}</div>
                <div class="stat-label">Com Prescrição</div>
              </div>
              <div class="stat-icon">
                <i class="fas fa-pills"></i>
              </div>
            </div>

            <div class="stat-card with-longterm">
              <div class="stat-content">
                <div class="stat-number">{{ residentsWithLongTerm }}</div>
                <div class="stat-label">Prescrição Longo Prazo</div>
              </div>
              <div class="stat-icon">
                <i class="fas fa-clock"></i>
              </div>
            </div>

            <div class="stat-card no-prescription">
              <div class="stat-content">
                <div class="stat-number">
                  {{ residentsWithoutPrescription }}
                </div>
                <div class="stat-label">Sem prescrição diária</div>
              </div>
              <div class="stat-icon">
                <i class="fas fa-exclamation-triangle"></i>
              </div>
            </div>
          </div>

          <div
            class="residents-grid"
            :class="{ 'has-active-card': resident_id_with_pending }"
          >
            <div
              v-for="resident in residents"
              :key="resident.id"
              class="resident-card"
              :class="{
                'status-ok':
                  resident.existPrescription &&
                  resident.pendingChecks.length === 0,
                'status-warning': !resident.existPrescription,
                'status-critical': resident.pendingChecks.length > 0,
                'highlight-active': resident.id == resident_id_with_pending,
              }"
            >
              <div class="resident-header">
                <div class="resident-avatar">
                  <img
                    :src="resident.photo_url || require('../assets/user.png')"
                    :alt="resident.name"
                    class="avatar-image"
                  />
                  <div
                    class="status-indicator"
                    :class="getStatusClass(resident)"
                  ></div>
                </div>

                <div class="resident-info">
                  <div class="resident-name">{{ resident.name }}</div>
                  <div class="resident-details">
                    <span v-if="resident.nickname" class="nickname">{{
                      resident.nickname
                    }}</span>
                    <span v-if="resident.date_of_birth && $store.state.online">
                      {{ getAge(resident.date_of_birth) }} anos
                    </span>
                  </div>
                </div>
              </div>

              <div class="status-badges">
                <div
                  class="badge"
                  :class="
                    resident.existPrescription
                      ? 'badge-success'
                      : 'badge-warning'
                  "
                >
                  <i class="fas fa-pills"></i>
                  <span>{{
                    resident.existPrescription
                      ? "Prescrição ativa"
                      : "Sem prescrição diária"
                  }}</span>
                </div>

                <div
                  v-if="resident.existPrescriptionLongTerm"
                  class="badge badge-info"
                >
                  <i class="fas fa-clock"></i>
                  <span>Prescrição longo prazo</span>
                </div>

                <div
                  v-if="resident.pendingChecks.length > 0"
                  class="badge badge-critical"
                >
                  <i class="fas fa-exclamation-triangle"></i>
                  <span>
                    {{ resident.pendingChecks.length }}
                    {{
                      resident.pendingChecks.length > 1
                        ? "Medicamentos"
                        : "Medicamento"
                    }}
                    {{
                      resident.pendingChecks.length > 1
                        ? "atrasados"
                        : "atrasado"
                    }}
                  </span>
                </div>

                <div
                  v-if="resident.pendingSemiology.length > 0"
                  class="badge badge-critical"
                >
                  <i class="fas fa-exclamation-triangle"></i>
                  <span>
                    {{ resident.pendingSemiology.length }}
                    {{
                      resident.pendingSemiology.length > 1
                        ? "Formulários"
                        : "Formulário"
                    }}
                    {{
                      resident.pendingSemiology.length > 1
                        ? "pendentes"
                        : "pendente"
                    }}
                  </span>
                </div>

                <div
                  v-for="(schedule, keySchedule) in resident.schedules"
                  :key="`${resident.id}-${keySchedule}`"
                  class="badge badge-info"
                >
                  <i class="fas fa-calendar-alt"></i>
                  <span>
                    {{ schedule.schedule_type_name }}

                    <span v-if="schedule.time_init && schedule.time_end">
                      {{
                        String(schedule.time_init).substring(
                          0,
                          String(schedule.time_init).length - 3
                        )
                      }}
                      às
                      {{
                        String(schedule.time_end).substring(
                          0,
                          String(schedule.time_end).length - 3
                        )
                      }}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="loadingMore && residents.length > 0" class="loading-more">
            <div class="loading-spinner">
              <i class="fas fa-spinner fa-spin"></i>
            </div>
            <span class="loading-text">Carregando mais residentes...</span>
          </div>

          <div
            v-if="residents.length > 0 && hasMorePages && !loadingMore"
            class="progress-indicator"
          >
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: progressPercentage + '%' }"
              ></div>
            </div>
            <div class="progress-text">
              {{ residents.length }} de {{ totalResidents }} residentes
              carregados
            </div>
          </div>

          <div v-if="residents.length == 0 && !loading" class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-users"></i>
            </div>
            <div class="empty-title">Nenhum residente encontrado</div>
            <div class="empty-text">
              Não há residentes para monitorar no momento.
            </div>
          </div>
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import DashboardMonitoringConfig from "../templates/DashboardMonitoring/Config";
import Terms from "../templates/User/terms";
import api from "../services/api";
import { getAge } from "../lib/getAgeByDate";
import { formatDate } from "../lib/format";
import { userData } from "../lib/functions";

export default {
  name: "DashboardMonitoring",

  components: {
    NavBar,
    AuthBySessionExpired,
    DashboardMonitoringConfig,
    Terms,
  },

  data() {
    return {
      terms: 0,
      nextUpdateText: "30m 60s",
      resident_id_with_pending: null,
      voice: 0,
      residents: [],
      loading: false,
      loadingMore: false,
      errors: [],
      currentPage: 1,
      itemsPerPage: 25,
      hasMorePages: true,
      totalResidents: 0,
      autoLoadTimeout: null,

      autoUpdateInterval: 30,
      nextUpdateTime: null,
      updateCountdown: null,
      countdownTimer: null,
    };
  },

  watch: {
    resident_id_with_pending(newId, oldId) {
      if (newId && newId !== oldId) {
        this.scrollToActiveResident();
      }
    },
  },

  computed: {
    residentsWithPrescription() {
      return this.residents.filter((r) => r.existPrescription).length;
    },

    residentsWithoutPrescription() {
      return this.residents.filter((r) => !r.existPrescription).length;
    },

    residentsWithLongTerm() {
      return this.residents.filter((r) => r.existPrescriptionLongTerm).length;
    },

    progressPercentage() {
      if (this.totalResidents === 0) return 0;
      return Math.round((this.residents.length / this.totalResidents) * 100);
    },
  },

  created() {
    this.retrieve();
    this.startCountdown();
  },

  beforeDestroy() {
    this.clearTimers();
  },

  methods: {
    getAge,
    formatDate,

    nextUpdateRetrieve() {
      if (!this.nextUpdateTime) {
        this.nextUpdateText = "Calculando...";
        return;
      }

      if (this.loading) {
        this.nextUpdateText = "Atualizando agora...";
        return;
      }

      let now = new Date().getTime();
      let timeLeft = this.nextUpdateTime - now;

      if (timeLeft <= 0) {
        this.nextUpdateText = "Atualizando...";
        return;
      }

      let minutes = Math.floor(timeLeft / (1000 * 60));
      let seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      if (minutes > 0) {
        this.nextUpdateText = `${minutes}m ${seconds}s`;
      } else {
        this.nextUpdateText = `${seconds}s`;
      }
    },

    clearTimers() {
      if (this.autoLoadTimeout) {
        clearTimeout(this.autoLoadTimeout);
      }
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
    },

    startCountdown() {
      this.nextUpdateTime =
        new Date().getTime() + this.autoUpdateInterval * 60 * 1000;

      this.countdownTimer = setInterval(() => {
        const now = new Date().getTime();
        const timeLeft = this.nextUpdateTime - now;

        if (timeLeft <= 0) {
          this.retrieve();
        }
      }, 1000);
    },

    resetCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      this.startCountdown();
    },

    scrollToActiveResident() {
      // Aguarda um pequeno delay para garantir que o DOM foi atualizado
      this.$nextTick(() => {
        setTimeout(() => {
          const activeCard = document.querySelector(
            ".resident-card.highlight-active"
          );

          if (activeCard) {
            // Define todas as variáveis necessárias primeiro
            const headerHeight =
              document.querySelector(".monitoring-header")?.offsetHeight || 0;
            const cardTop = activeCard.offsetTop;
            const cardHeight = activeCard.offsetHeight;
            const windowHeight = window.innerHeight;

            // Verifica se o card já está visível na viewport
            const rect = activeCard.getBoundingClientRect();
            const isVisible =
              rect.top >= headerHeight && rect.bottom <= windowHeight;

            // Só faz scroll se o card não estiver completamente visível
            if (!isVisible) {
              // Calcula a posição ideal para centralizar o card na viewport
              const idealScrollPosition =
                cardTop - windowHeight / 2 + cardHeight / 2 + headerHeight;

              // Faz scroll suave até o card
              window.scrollTo({
                top: Math.max(0, idealScrollPosition),
                behavior: "smooth",
              });
            }
          }
        }, 150); // Delay otimizado para sincronizar com a animação
      });
    },

    getStatusClass(resident) {
      if (resident.pendingChecks.length > 0) return "status-critical";
      if (!resident.existPrescription) return "status-warning";
      return "status-ok";
    },

    save({ voice, autoUpdateInterval = 30 }) {
      this.voice = voice;
      this.autoUpdateInterval = autoUpdateInterval;
      this.resetCountdown();
      this.$bvModal.hide("DashboardMonitoring");
    },

    handleStopSpeak() {
      speechSynthesis.cancel();
      this.resident_id_with_pending = null;
    },

    handleModalConfig() {
      this.$bvModal.show("DashboardMonitoring");
    },

    async toSpeak({
      text,
      resident_id = null,
      residentWithoutPrescription = false,
      residentWithPendingChecks = false,
      residentWithSchedules = false,
      residentWithPendingSemiologys = false,
    }) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "pt-BR";
      utterance.rate = 1.2; // Velocidade
      utterance.volume = 1;
      utterance.pitch = 1;

      let voice =
        localStorage.getItem("sistemaclin_dashboard_monitoring_voice") || 0;
      utterance.voice = speechSynthesis.getVoices()[voice];
      speechSynthesis.speak(utterance);

      utterance.onstart = () => {
        if (
          residentWithoutPrescription ||
          residentWithPendingChecks ||
          residentWithSchedules ||
          residentWithPendingSemiologys
        ) {
          this.resident_id_with_pending = resident_id;
        }
      };

      utterance.onend = () => {
        this.resident_id_with_pending = null;
      };
    },

    async toSpeaks() {
      await this.toSpeakResidentsWithoutPrescription();
    },

    async toSpeakResidentsWithoutPrescription() {
      let { residents } = this;

      for (let resident of residents) {
        if (!resident.existPrescription) {
          let text = `Residente ${resident.name} não possuí prescrição ativa!`;
          await this.toSpeak({
            text,
            resident_id: resident.id,
            residentWithoutPrescription: true,
          });
        }

        if (resident.pendingChecks.length > 0) {
          let text = `Residente ${resident.name} com ${resident.pendingChecks.length}`;
          text +=
            resident.pendingChecks.length == 1
              ? ` medicamento atrasado!`
              : ` medicamentos atrasados!`;

          await this.toSpeak({
            text,
            resident_id: resident.id,
            residentWithPendingChecks: true,
          });
        }

        if (resident.pendingSemiology.length > 0) {
          let text = `Residente ${resident.name} com ${resident.pendingSemiology.length}`;
          text +=
            resident.pendingSemiology.length == 1
              ? ` formulário pendente!`
              : ` formulários pendentes!`;

          await this.toSpeak({
            text,
            resident_id: resident.id,
            residentWithPendingChecks: true,
          });
        }

        if (resident.schedules.length > 0) {
          for (let schedule of resident.schedules) {
            let text = `Residente ${resident.name} com agendamento hoje ${schedule.schedule_type_name}`;

            if (schedule.time_init && schedule.time_end) {
              text += ` às ${schedule.time_init} até ${schedule.time_end}`;
            }

            await this.toSpeak({
              text,
              resident_id: resident.id,
              residentWithSchedules: true,
            });
          }
        }
      }
    },

    async retrieve() {
      let { terms } = userData().user;

      this.terms = terms;

      if (terms == 1) {
        this.clearTimers();
        this.currentPage = 1;
        this.residents = [];
        this.hasMorePages = true;

        await this.getResidents(true);
        this.resetCountdown();

        let _this = this;

        setInterval(() => _this.nextUpdateRetrieve(), 1000);
      }
    },

    async getResidents(isRefresh = false) {
      try {
        if (isRefresh) {
          this.loading = true;
        } else {
          this.loadingMore = true;
        }

        const params = {
          page: this.currentPage,
          itemsPerPage: this.itemsPerPage,
        };

        const response = await api.get("/rest-home/dashboard/monitoring", {
          params,
        });

        const { residents, total, current_page, last_page } = response.data;

        if (isRefresh) {
          this.residents = residents;
        } else {
          this.residents = [...this.residents, ...residents];
        }

        this.totalResidents = total || this.residents.length;
        this.hasMorePages = current_page < last_page;

        this.loading = false;
        this.loadingMore = false;

        if (this.hasMorePages) {
          this.autoLoadTimeout = setTimeout(() => {
            this.loadMoreResidents();
          }, 1000);
        } else {
          this.toSpeaks();
          this.totalResidents = this.residents.length;
        }
      } catch (error) {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    async loadMoreResidents() {
      if (this.loadingMore || !this.hasMorePages) {
        return;
      }

      this.currentPage++;
      await this.getResidents(false);
    },
  },
};
</script>

<style scoped>
.monitoring-dashboard {
  min-height: 100vh;
  background: #f8fafc;
  font-family: "WorkSans-Regular", sans-serif;
}

.monitoring-container {
  margin: 0 auto;
  padding: 0 20px;
}

/* Header */
.monitoring-header {
  padding: 16px 0;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 8px;
  font-size: 20px;
}

.page-subtitle {
  color: #64748b;
  margin: 2px 0 0 0;
  font-size: 14px;
}

/* Auto Update Info */
.auto-update-info {
  flex: 1;
  display: flex;
  justify-content: center;
}

.update-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f9;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ef4444;
  animation: pulse 2s infinite;
}

.indicator-dot.active {
  background: #10b981;
  animation: none;
}

.update-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.update-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.update-time {
  font-size: 13px;
  color: #1e293b;
  font-weight: 600;
  font-family: "Courier New", monospace;
}

/* Action Buttons */
.actions-section {
  display: flex;
  gap: 8px;
}

.btn-modern {
  border-radius: 8px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  font-size: 12px !important;
  padding: 8px 12px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}

.btn-modern-config {
  background: #f1f5f9 !important;
  color: #475569 !important;
  border: 1px solid #e2e8f0 !important;
}

.btn-modern-refresh {
  background: #0f65b0 !important;
  color: white !important;
  border: none !important;
}

.btn-modern-stop {
  background: #f59e0b !important;
  color: white !important;
  border: none !important;
}

.btn-modern:hover {
  transform: translateY(-1px) !important;
}

/* Content */
.monitoring-content {
  padding: 20px 0;
}

.loading-overlay {
  border-radius: 12px;
}

/* Stats Summary */
.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.stat-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  opacity: 0.8;
}

.total-residents .stat-icon {
  background: #f1f5f9;
  color: #475569;
}

.with-prescription .stat-icon {
  background: #ecfdf5;
  color: #059669;
}

.with-longterm .stat-icon {
  background: #eff6ff;
  color: #2563eb;
}

.no-prescription .stat-icon {
  background: #fffbeb;
  color: #d97706;
}

/* Residents Grid */
.residents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  position: relative;
}

.residents-grid.has-active-card {
  position: relative;
}

.residents-grid.has-active-card::before {
  content: "";
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 101, 176, 0.02);
  backdrop-filter: blur(0.5px);
  z-index: 1;
  pointer-events: none;
  opacity: 0;
  animation: overlayFadeIn 0.3s ease-out forwards;
}

.resident-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.resident-card.status-ok {
  border-left: 4px solid #10b981;
}

.resident-card.status-warning {
  border-left: 4px solid #f59e0b;
}

.resident-card.status-critical {
  border-left: 4px solid #ef4444;
}

.resident-card.highlight-active {
  animation: cardBounceHighlight 1.5s ease-in-out infinite;
  transform: scale(1.02);
  border: 1px solid #f1f5f9 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.3),
    0 0 20px rgba(15, 101, 176, 0.5), 0 8px 25px rgba(15, 101, 176, 0.2);
  z-index: 10;
  position: relative;
  background: white;
}

.resident-card.highlight-active::before {
  display: none;
}

.resident-card.highlight-active::after {
  display: none;
}

.residents-grid.has-active-card .resident-card:not(.highlight-active) {
  opacity: 0.7;
  filter: blur(0.3px);
  transform: scale(0.99);
  transition: all 0.3s ease;
}

.residents-grid.has-active-card .resident-card.highlight-active {
  z-index: 15;
}

/* Resident Header */
.resident-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.resident-avatar {
  position: relative;
  flex-shrink: 0;
}

.avatar-image {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f1f5f9;
}

.status-indicator {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid white;
}

.status-indicator.status-ok {
  background: #10b981;
}

.status-indicator.status-warning {
  background: #f59e0b;
}

.status-indicator.status-critical {
  background: #ef4444;
}

.resident-info {
  flex: 1;
  min-width: 0;
}

.resident-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resident-details {
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
}

.nickname {
  font-style: italic;
}

/* Status Badges */
.status-badges {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid transparent;
}

.badge i {
  font-size: 10px;
}

.badge-success {
  background: #ecfdf5;
  color: #059669;
  border-color: #bbf7d0;
}

.badge-warning {
  background: #fffbeb;
  color: #d97706;
  border-color: #fed7aa;
}

.badge-info {
  background: #eff6ff;
  color: #2563eb;
  border-color: #bfdbfe;
}

.badge-critical {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

/* Loading More */
.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  margin: 16px 0;
  background: white;
  border-radius: 8px;
  border: 1px dashed #cbd5e1;
}

.loading-spinner i {
  font-size: 14px;
  color: #0f65b0;
}

.loading-text {
  font-size: 13px;
  color: #64748b;
}

/* Progress Indicator */
.progress-indicator {
  margin: 20px 0;
  text-align: center;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: #f1f5f9;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #0f65b0, #3b82f6);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 12px;
  color: #64748b;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #f1f5f9;
}

.empty-icon {
  font-size: 48px;
  color: #cbd5e1;
  margin-bottom: 16px;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.empty-text {
  font-size: 14px;
  color: #64748b;
}

/* Animations */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes cardBounceHighlight {
  0%,
  100% {
    transform: scale(1.02);
    box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.3),
      0 0 20px rgba(15, 101, 176, 0.5), 0 8px 25px rgba(15, 101, 176, 0.2);
  }
  50% {
    transform: scale(1.04);
    box-shadow: 0 0 0 5px rgba(15, 101, 176, 0.4),
      0 0 30px rgba(15, 101, 176, 0.6), 0 12px 35px rgba(15, 101, 176, 0.3);
  }
}

@keyframes overlayFadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.fa-spin {
  animation: fa-spin 1s infinite linear;
}

@keyframes fa-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    flex-direction: column;
    gap: 12px;
  }

  .auto-update-info {
    order: -1;
  }

  .residents-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .monitoring-container {
    padding: 0 16px;
  }

  .stats-summary {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-number {
    font-size: 24px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .residents-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .resident-card {
    padding: 12px;
  }

  .avatar-image {
    width: 40px;
    height: 40px;
  }

  .status-indicator {
    width: 12px;
    height: 12px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
  }

  .actions-section {
    flex-wrap: wrap;
    gap: 6px;
  }

  .stats-summary {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .update-indicator {
    padding: 6px 12px;
    font-size: 11px;
  }

  .resident-header {
    gap: 8px;
  }

  .resident-name {
    font-size: 14px;
  }

  .badge {
    font-size: 10px;
    padding: 4px 8px;
  }
}
</style>